import React, { useEffect, useState } from 'react';
import { Loader2, Users } from 'lucide-react';
import { onDutyApi } from '../api/onDuty.api';
import OnDutyAvatar from './OnDutyAvatar';
import OnDutyProfileCard from './OnDutyProfileCard';
import { IITH, SERIF } from '@/pages/landing/iithBrand';

/** Dashboard strip: who is on published duty today (icons + short profile). */
export default function OnDutyTodayStrip({ className = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    onDutyApi.today()
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ people: [], count: 0, date: '' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const people = data?.people || [];
  const selected = people.find((p) => p.staff_id === selectedId) || null;

  return (
    <section
      className={`rounded-2xl border overflow-hidden bg-white ${className}`}
      data-testid="on-duty-today-strip"
    >
      <div
        className="px-5 py-4 flex flex-wrap items-end justify-between gap-3"
        style={{
          background: `linear-gradient(120deg, ${IITH.navy} 0%, ${IITH.navyMid} 60%, #123556 100%)`,
        }}
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: IITH.gold }}>
            On duty today
          </p>
          <h3 className="text-xl font-semibold text-white mt-0.5" style={{ fontFamily: SERIF }}>
            Who&apos;s available
          </h3>
          <p className="text-xs mt-1" style={{ color: IITH.mute }}>
            Published roster · {data?.date || '—'} · Asia/Kolkata
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-white text-sm">
          <Users className="h-4 w-4" />
          {loading ? '…' : `${data?.count ?? 0} on duty`}
        </div>
      </div>

      <div className="p-4 sm:p-5">
        {loading ? (
          <div className="py-8 text-center text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin inline" />
          </div>
        ) : people.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center">
            No published duties for today. Publish a roster to see staff here.
          </p>
        ) : (
          <>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
              {people.map((p) => (
                <OnDutyAvatar
                  key={p.staff_id}
                  person={p}
                  selected={selectedId === p.staff_id}
                  onClick={() => setSelectedId(
                    selectedId === p.staff_id ? null : p.staff_id,
                  )}
                />
              ))}
            </div>
            {selected && (
              <div className="mt-4">
                <OnDutyProfileCard person={selected} onClose={() => setSelectedId(null)} />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
